import * as yup from "yup";
import { KeyValueMap } from "../../../../redux/slices/appState/models";
import { CreateGenericAccountFormKey } from "./Models";
import dayjs from "dayjs";
import {
  DATE_FORMAT,
  GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES,
} from "../../../../helpers/constants";
import { isNumberBetweenRangeValidation } from "../../../../helpers/constants/commonValidationSchemaUtils";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";

export const CreateGenericAccountValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape(
    {
      [CreateGenericAccountFormKey.genericAccountName]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.purpose]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.clientId]: yup
        .string()
        .when([CreateGenericAccountFormKey.purpose], {
          is: (purpose: string) => {
            return (
              purpose === GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES.CLIENT
            );
          },
          then: (schema) => {
            return schema.required(commonValidationMessages.requiredValidation);
          },
        }),
      [CreateGenericAccountFormKey.genericRegistrationCode]: yup
        .string()
        .required(commonValidationMessages.requiredValidation)
        .min(20, commonValidationMessages.minimumOf20CharactersRequired),
      [CreateGenericAccountFormKey.numberOfAccounts]:
        isNumberBetweenRangeValidation(
          0,
          500,
          commonValidationMessages.pleaseEnterNumberBetween1To500,
        ).required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.programDuration]:
        isNumberBetweenRangeValidation(
          0,
          600,
          commonValidationMessages.pleaseEnterNumberBetween1To600,
        ).required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.requestedBy]: yup.string(),
      [CreateGenericAccountFormKey.nextView]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.country]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.deliveryOffice]: yup
        .array()
        .min(1, commonValidationMessages.requiredValidation)
        .required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.nextProgramClass]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [CreateGenericAccountFormKey.features]: yup.array(),
      [CreateGenericAccountFormKey.status]: yup
        .string()
        .default(STATUS_BADGE_TYPE.active),
      [CreateGenericAccountFormKey.expirationDate]: yup
        .string()
        .required(commonValidationMessages.requiredValidation)
        .optional()
        .default("")
        .when([CreateGenericAccountFormKey.startDate], {
          is: (startDate: string) => {
            return !!startDate;
          },
          then: (schema) => {
            return schema.test(
              "is-before",
              commonValidationMessages.expirationDateMustBeGreaterThanStartDateErrorMessage,
              (_, value) => {
                const startDateWithoutFormatted = dayjs(
                  value.parent?.startDateWithoutFormatted?.format(
                    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                  ),
                );
                const expiryDateWithoutFormatted = dayjs(
                  value.parent?.expirationDateWithoutFormatted?.format(
                    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                  ),
                );
                return startDateWithoutFormatted?.isBefore(
                  expiryDateWithoutFormatted,
                );
              },
            );
          },
        }),
      [CreateGenericAccountFormKey.startDate]: yup
        .string()
        .required(commonValidationMessages.requiredValidation)
        .default("")
        .when([CreateGenericAccountFormKey.expirationDate], {
          is: (expiryDate: string) => {
            return !!expiryDate;
          },
          then: (schema) => {
            return schema.test(
              "is-after",
              commonValidationMessages.startDateMustBeLesserThanExpirationDateErrorMessage,
              (_, value) => {
                const startDateWithoutFormatted = dayjs(
                  value.parent?.startDateWithoutFormatted?.format(
                    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                  ),
                );
                const expiryDateWithoutFormatted = dayjs(
                  value.parent?.expirationDateWithoutFormatted?.format(
                    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                  ),
                );
                return expiryDateWithoutFormatted?.isAfter(
                  startDateWithoutFormatted,
                );
              },
            );
          },
        }),
      [CreateGenericAccountFormKey.startDateWithoutFormatted]: yup
        .object()
        .nullable(),
      [CreateGenericAccountFormKey.expirationDateWithoutFormatted]: yup
        .object()
        .nullable(),
    },
    [
      [
        CreateGenericAccountFormKey.startDate,
        CreateGenericAccountFormKey.expirationDate,
      ],
    ],
  );
